import Icon, { LoadingOutlined } from '@ant-design/icons';
import { ESessionType } from '@mindverse/accessor-open';
import type { SocketSessionManager } from '@mindverse/accessor-open';
import { Alignment, Fit, Layout, useRive } from '@rive-app/react-webgl2';
import { message } from 'antd';
import { type MentionsRef } from 'antd/es/mentions';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import {
  type KeyboardEvent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import cookie from 'react-cookies';

import LetMeRive from '@/assets/Letme_v3.riv';
import AddSvg from '@/components/Icon/svgs/add.svg';
import ChatSvg from '@/components/Icon/svgs/chat.svg';
import KeyboardSvg from '@/components/Icon/svgs/keyboard.svg';
import RecordingSvg from '@/components/Icon/svgs/mic_unfill.svg';
import SendSvg from '@/components/Icon/svgs/plane_send.svg';
import ReturnSVG from '@/components/Icon/svgs/return.svg';
import RightSvg from '@/components/Icon/svgs/right.svg';
import { useOpenAIRealtimeTranscription } from '@/hooks/useOpenAIRealtimeTranscription';
import { useTranslation } from '@/i18n/client';
import { HOMEPAGE_FRIEND_STATUS } from '@/service/homePage';
import { EGlobalCookieKey } from '@/types/cookie';
import { useCircleChatContext } from '@/ui/CircleChat/CircleChatContext';
import { useHomepageContext } from '@/ui/HomePage/HomepageContext';
import { EVENT } from '@/utils/event';
import { innerHost } from '@/utils/host';
import getTimezone from '@/utils/timezone';

import ContextBasicInfo from '../../context/BasicInfo';
import type { SendMsgData } from '../../hooks/useChatSession';
import useMindStorage, { EStorageKey } from '../../hooks/useMindStorage';
import type { MessageModel } from '../../hooks/useSessionMsg';
import type { Action } from '../../types/common';
import { EChatAppIds } from '../../utils/const';
import { CONTAINER_EVENT } from '../../utils/utils';
import AddPopover from './AddPopover';
import MultimodalInput from './MultimodalInput';
import { HighlightInTextarea } from './high-light';
import styles from './index.less';
import type { QuickNoteType } from './quickNote';
import useInput from './useInput';
import type { FileItem } from './useUpload';
import useUpload, { UpStatus } from './useUpload';

// Lazy load conditional components - only loaded when needed
const SmallDotsLoading = dynamic(() => import('../SmallDotsLoading'), {
  ssr: false
});
const QuoteContainer = dynamic(() => import('../chat/components/SendQuoteContainer'), {
  ssr: false
});
const AutoReply = dynamic(() => import('./OthersAutoReply'), {
  ssr: false
});
const IceBreak = dynamic(() => import('./IceBreak'), {
  ssr: false
});
const QuickNote = dynamic(() => import('./quickNote'), {
  ssr: false
});

interface IProps {
  initialType?: 'keyboard' | 'mic';
  isPA: boolean;
  sessionManager?: SocketSessionManager;
  onSendMsg?: (
    msgData: SendMsgData,
    params: { sessionId?: string; voiceChannel?: boolean; anonymous?: boolean }
  ) => undefined | Promise<boolean>;
  inChatMode: boolean;
  inMobile: boolean;
  needLogin: boolean;
  msgLoading: boolean;
  userId: string | number;
  uploadType?: string;
  onFocusChanged: (isFocus: boolean) => void;
  hasInitShortcut: boolean | undefined;
  hideQuickNote?: boolean;
  msgList?: MessageModel[];
}

function UserInput(props: IProps): JSX.Element {
  const {
    initialType = 'keyboard',
    isPA,
    onSendMsg,
    inChatMode,
    needLogin,
    userId,
    inMobile,
    msgLoading,
    onFocusChanged,
    uploadType,
    hideQuickNote,
    sessionManager,
    msgList
  } = props;

  const [sessionInfo] = useMindStorage(EStorageKey.SESSION_INFO);
  const [sessionId] = useMindStorage(EStorageKey.SESSION_ID);
  const { t } = useTranslation();

  const showQuickNote = useMemo(() => {
    return isPA && !hideQuickNote && sessionInfo?.type == ESessionType.MEBOT;
  }, [isPA, hideQuickNote, sessionInfo]);

  const maxFiles = 10;
  const hitRef = useRef<{ destroy: () => void } | null>(null);
  const textareaRef = useRef<MentionsRef>();
  const [shortcutShow, _setShortcutShow] = useState<boolean>(true);
  const [takeoverLoading, setTakeoverLoading] = useState(false);
  const [historyValues, setHistoryValues] = useState<string[]>([]);
  const [tempValueIndex, setTempValueIndex] = useState(-1);
  const tempValueIndexRef = useRef(-1);

  // Unified push to history function
  // - Truncates history after current index (branching logic)
  // - Skips if value equals the item at current index
  const pushToHistory = (value: string) => {
    setHistoryValues((prev) => {
      const currentIndex = tempValueIndexRef.current;
      const currentValue = prev[currentIndex];

      // If value equals current position value, skip
      if (currentValue === value) {
        return prev;
      }

      // Truncate history after current index (discard future branch)
      const truncated = prev.slice(0, currentIndex + 1);
      const newHistory = [...truncated, value];
      const newIndex = newHistory.length - 1;

      tempValueIndexRef.current = newIndex;
      setTempValueIndex(newIndex);

      return newHistory;
    });
  };
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingTextRef = useRef('');
  const typingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamEndedRef = useRef(false);
  const [typingLabel, setTypingLabel] = useState('');
  const typingLabelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const takeoverBubbleRef = useRef<HTMLSpanElement>(null);
  const isSummaryRef = useRef(false);

  const [type, setType] = useState<'keyboard' | 'mic'>(initialType);

  const [enabled, setEnabled] = useState<boolean>(true);

  const [stateText, setStateText] = useState<string>('You can speak now');

  const originStreamTextRef = useRef('');
  const savedInputValueRef = useRef(''); // 保存接口调用前的原始 inputValue

  const { chatType } = useContext(ContextBasicInfo);

  const [quote, setQuote] = useMindStorage(EStorageKey.QUOTE);

  const [inputShowLogin, setInputShowLogin] = useMindStorage(EStorageKey.INPUT_SHOW_LOGIN);

  const { homepageData } = useHomepageContext();
  const { circleDetail } = useCircleChatContext();
  const [summaryVisible, setSummaryVisible] = useState(true);

  const hasEnoughUnreadMessages = useMemo(() => {
    if (!msgList?.length) {
      return false;
    }

    const threshold = 10;
    let count = 0;

    for (const group of msgList) {
      for (const item of group.list) {
        if ('messageStatus' in item && item.messageStatus === 'UNREAD') {
          count++;

          if (count >= threshold) {
            return true;
          }
        }
      }
    }

    return false;
  }, [msgList]);

  const showSummaryButton =
    summaryVisible && (!!circleDetail?.route || !!homepageData?.route) && hasEnoughUnreadMessages;

  // Rive animation state: 0=idle, 1=working, 2=raise hand
  const [riveStatus, setRiveStatus] = useState<-1 | 0 | 1 | 2>(-1);
  const riveReadyRef = useRef(false);

  // Rive animation setup
  const { RiveComponent, rive } = useRive(
    {
      src: LetMeRive,
      autoplay: true,
      artboard: 'wolai',
      stateMachines: 'state',
      layout: new Layout({
        fit: Fit.Cover,
        alignment: Alignment.Center
      })
    },
    {
      fitCanvasToArtboardHeight: true,
      useDevicePixelRatio: true,
      shouldResizeCanvasToContainer: true
    }
  );

  // Fire coming animation when component mounts and rive is ready
  useEffect(() => {
    if (rive && !riveReadyRef.current) {
      const inputs = rive.stateMachineInputs('state');

      const comingTrigger = inputs.find((input) => input.name === 'coming');

      if (comingTrigger) {
        comingTrigger.fire();
        riveReadyRef.current = true;
        setTimeout(() => {
          setRiveStatus(0);
        }, 1500);
      }
    }
  }, [rive]);

  // Update rive status input when riveStatus changes
  useEffect(() => {
    if (rive) {
      const inputs = rive.stateMachineInputs('state');
      const statusInput = inputs.find((input) => input.name === 'status');

      if (statusInput) {
        statusInput.value = riveStatus;
      }
    }
  }, [rive, riveStatus]);

  // Update riveStatus based on typingLabel, takeoverLoading and showSummaryButton
  useEffect(() => {
    if (!riveReadyRef.current) return;

    if (takeoverLoading) {
      // Working state when loading
      setRiveStatus(1);
    } else if (typingLabel.length > 0 || showSummaryButton) {
      // Raise hand state when there's a label (bubble showing) or summary button is visible
      setRiveStatus(2);
    } else {
      // Idle state
      setRiveStatus(0);
    }
  }, [takeoverLoading, typingLabel, showSummaryButton]);

  // Bubble 关闭状态管理
  // const [isBubbleClosed, setIsBubbleClosed] = useState(false);

  // 检查当前 bubbleId 是否被用户关闭过（24小时内）
  // const checkIsClosed = useCallback(() => {
  //   if (!bubbleData?.bubbleId) return false;

  //   if (bubbleData.isUsed) return true;

  //   const STORAGE_KEY = 'closedBubbleIds';
  //   const EXPIRE_TIME = 24 * 60 * 60 * 1000;

  //   const closedBubbles = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  //   const closedTime = closedBubbles[bubbleData.bubbleId];

  //   if (!closedTime) return false;

  //   if (Date.now() - closedTime >= EXPIRE_TIME) {
  //     delete closedBubbles[bubbleData.bubbleId];
  //     localStorage.setItem(STORAGE_KEY, JSON.stringify(closedBubbles));

  //     return false;
  //   }

  //   return true;
  // }, [bubbleData]);

  // useEffect(() => {
  //   setIsBubbleClosed(checkIsClosed());
  // }, [checkIsClosed]);

  // 重置一下强制关闭的状态
  useEffect(() => {
    if (circleDetail?.route) {
      setSummaryVisible(true);
    }
  }, [circleDetail?.route, sessionId]);

  // 玩法功能的公共判断条件
  const showHomepageFriendBtns = useMemo(
    () =>
      chatType === 'homepage' &&
      homepageData &&
      !['official_product_assistant'].includes(homepageData.route || '') &&
      homepageData.friendStatus === HOMEPAGE_FRIEND_STATUS.FRIEND &&
      !homepageData.isBlocked &&
      !homepageData.blockedByMe &&
      userId,
    [chatType, homepageData, userId]
  );

  const handleHideSummary = useCallback(() => {
    setSummaryVisible(false);
  }, []);

  const clearQuote = () => {
    setQuote(undefined);
  };

  // 上传逻辑
  const {
    upList,
    handleUploadClick,
    handleUploadChange,
    handleDelFile,
    clearUpList,
    handleUploadFiles
  } = useUpload(userId, uploadType, maxFiles);

  // input 逻辑
  const {
    handleInputChange,
    handleOnBlur,
    handleOnFocus,
    handlePressEnter,
    handleIconSend,
    handleOnPaste,
    inputValue,
    setInputValue,
    onMentionSelect,
    sendMsgLoading,
    isFocus,
    handleSendMsg
  } = useInput({
    inChatMode,
    sessionManager,
    needLogin,
    msgLoading: msgLoading && sessionInfo?.type == ESessionType.MEBOT,
    upList,
    clearUpList,
    onSendMsg,
    handleUploadFiles,
    quote,
    clearQuote,
    textareaRef
  });

  const [anonymousInitLoading] = useMindStorage(EStorageKey.ANONYMOUS_INIT_LOADING);

  useEffect(() => {
    if (homepageData?.isLogin) {
      setInputShowLogin(false);
    }
  }, [homepageData]);

  useEffect(() => {
    tempValueIndexRef.current = tempValueIndex;
  }, [tempValueIndex]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
      }

      if (typingLabelTimerRef.current) {
        clearTimeout(typingLabelTimerRef.current);
        typingLabelTimerRef.current = null;
      }
    };
  }, []);

  const setInputValueAuto = (value: string) => {
    const event = new CustomEvent(CONTAINER_EVENT.SET_INPUT_VALUE, {
      detail: value || ''
    });

    dispatchEvent(event);
  };

  const handleBlur = () => {
    const currentValue = historyValues[tempValueIndex];

    // If value unchanged, skip
    if (inputValue === currentValue) {
      return;
    }

    // Push edited value to history
    pushToHistory(inputValue);
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    pendingTextRef.current = '';
    streamEndedRef.current = false;

    setTakeoverLoading(false);
    setTypingLabel('');
    isSummaryRef.current = false;

    // 如果还没有收到数据，恢复原始 inputValue
    if (savedInputValueRef.current && !originStreamTextRef.current) {
      setInputValue(savedInputValueRef.current);
      savedInputValueRef.current = '';

      return;
    }

    savedInputValueRef.current = '';

    // If no content generated, restore previous value
    if (!inputValue) {
      const currentValue = historyValues[tempValueIndexRef.current] || '';

      setInputValueAuto(currentValue);

      return;
    }

    // Push generated content to history
    pushToHistory(inputValue);
  };

  const handlePrevVersion = () => {
    if (tempValueIndexRef.current <= 0) return;

    // Save current edit before navigating
    handleBlur();

    const prevIndex = tempValueIndexRef.current - 1;

    tempValueIndexRef.current = prevIndex;
    setTempValueIndex(prevIndex);
    setInputValueAuto(historyValues[prevIndex] || '');
  };

  const handleNextVersion = () => {
    if (tempValueIndexRef.current >= historyValues.length - 1) return;

    const nextIndex = tempValueIndexRef.current + 1;

    setTempValueIndex(nextIndex);
    tempValueIndexRef.current = nextIndex;
    setInputValueAuto(historyValues[nextIndex] || '');
  };

  const appendCharToOutput = (char: string) => {
    if (!char) return;

    setInputValue((prev) => `${prev || ''}${char}`);
  };

  const ensureTypingTimer = () => {
    if (typingTimerRef.current) return;

    typingTimerRef.current = setInterval(() => {
      if (!pendingTextRef.current) {
        if (streamEndedRef.current) {
          clearInterval(typingTimerRef.current!);
          typingTimerRef.current = null;
          streamEndedRef.current = false;
          setTakeoverLoading(false);
          setTypingLabel('');
          isSummaryRef.current = false;
          // 清空保存的原始值
          savedInputValueRef.current = '';

          // Push generated content to history when stream ends
          setInputValue((currentInput) => {
            if (currentInput) {
              pushToHistory(currentInput);
              setInputValueAuto(currentInput);
            } else {
              // No content generated, restore previous value
              const currentValue = historyValues[tempValueIndexRef.current] || '';

              setInputValueAuto(currentValue);
            }

            return currentInput;
          });
        }

        return;
      }

      const nextChar = pendingTextRef.current.charAt(0);

      pendingTextRef.current = pendingTextRef.current.slice(1);

      appendCharToOutput(nextChar);
    }, 10);
  };

  const handleTakeoverStream = async () => {
    if (takeoverLoading) return;

    handleHideSummary();

    if (originStreamTextRef.current && inputValue.trim() === originStreamTextRef.current.trim()) {
      setTypingLabel(t('homepage.addSomeThing'));

      // 清除之前的定时器
      if (typingLabelTimerRef.current) {
        clearTimeout(typingLabelTimerRef.current);
      }

      // 1.5秒后自动消失
      typingLabelTimerRef.current = setTimeout(() => {
        setTypingLabel('');
        typingLabelTimerRef.current = null;
      }, 1500);

      return;
    }

    // Ensure initial state is in history before first generation
    if (historyValues.length === 0) {
      pushToHistory(inputValue || '');
    } else {
      handleBlur();
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    pendingTextRef.current = '';
    streamEndedRef.current = false;
    originStreamTextRef.current = '';

    // 保存当前的 inputValue
    savedInputValueRef.current = inputValue;

    // setInputValueAuto('');
    setTakeoverLoading(true);
    // setTypingLabel(t('chatApp.taptap'));
    abortControllerRef.current = new AbortController();

    const token = cookie.load(EGlobalCookieKey.token);
    const baseUrl = innerHost();
    const timezone = getTimezone();

    try {
      const response = await fetch(`${baseUrl}/rest/os/takeover/execute/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: token,
          lang: cookie.load(EGlobalCookieKey.userLanguage) || 'en',
          ...(timezone ? { timeZone: timezone } : {})
        },
        body: JSON.stringify({
          scene: 'visitorChat',
          sceneId: sessionId,
          editText: isSummaryRef.current ? t('circle.summarizeMessage') : inputValue || '',
          quoteContent: quote?.data.content || '',
          quote: quote
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No reader available');
      }

      let buffer = '';
      let isDone = false;

      while (!isDone) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();

        if (done) {
          isDone = true;
          streamEndedRef.current = true;
          ensureTypingTimer();

          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');

        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;

          if (line.startsWith('data:')) {
            const data = line.slice(5).trim();

            if (!data) {
              continue;
            }

            if (data === '[DONE]') {
              continue;
            }

            try {
              const parsedData = JSON.parse(data);
              const messageContent = parsedData.content;

              if (messageContent) {
                // 第一次收到数据时才清空 inputValue
                if (!originStreamTextRef.current) {
                  setInputValue('');
                }

                originStreamTextRef.current += messageContent;
                pendingTextRef.current += messageContent;
                ensureTypingTimer();
              }
            } catch (error) {
              message.error(t('error.oops'));
            }
          }
        }
      }

      abortControllerRef.current = null;
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request aborted
      } else {
        console.error('Takeover SSE Error:', error);
      }

      abortControllerRef.current = null;
      setTakeoverLoading(false);
      setTypingLabel('');
      isSummaryRef.current = false;
      pendingTextRef.current = '';
      streamEndedRef.current = false;
      originStreamTextRef.current = '';

      // 恢复原始 inputValue
      if (savedInputValueRef.current) {
        setInputValue(savedInputValueRef.current);
        savedInputValueRef.current = '';
      }

      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
      }
    }
  };

  const highlight = (text: string) => {
    return new HighlightInTextarea('.rc-textarea', {
      once: true,
      highlight: text
    });
  };

  const setInputPrefix = (action: Action) => {
    const { value } = action;

    handleInputChange(value + ' ' + inputValue);
    hitRef.current?.destroy();
    setTimeout(() => {
      hitRef.current = highlight(value);
    }, 100);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 500);
  };

  const handleShortCutClick = (item: QuickNoteType) => {
    const { action } = item;
    const { type: actionType, value } = action;

    if (actionType === 'SET_INPUT_PREFIX') {
      return setInputPrefix(action);
    }

    if (actionType === 'SET_INPUT') {
      handleHideSummary();
      setInputValue(value + ' ');
      handleSendMsg(value);
    }
  };

  const handlePressEnterWithSummary = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      handleHideSummary();
      handlePressEnter(event);
    },
    [handleHideSummary, handlePressEnter]
  );

  const handleIconSendWithSummary = useCallback(() => {
    handleHideSummary();
    handleIconSend();
  }, [handleHideSummary, handleIconSend]);

  useEffect(() => {
    if (!inputValue) {
      hitRef.current?.destroy();
    }
  }, [inputValue]);

  useEffect(() => {
    onFocusChanged(isFocus);
  }, [isFocus, onFocusChanged]);

  // 不允许发送：1.有文件loading; 2.没有输入框值且没有成功文件

  const hideSend =
    upList.some((file: FileItem) => file.status == UpStatus.LOADING) ||
    (!inputValue.trim() && upList.every((file: FileItem) => file.status !== UpStatus.SUCCESS));

  const showAddIcon = userId && upList.length < 10;

  const handleEvent = useCallback(
    (event: any) => {
      if (
        event.type !== 'response.text.delta' &&
        event.type !== 'response.audio_transcript.delta'
      ) {
        console.log('onMessage:', event.type, event);
      }

      switch (event.type) {
        case 'input_audio_buffer.speech_started':
          setStateText("I'm listening...");

          break;
        case 'input_audio_buffer.committed':
          setStateText('Thinking...');

          break;
        case 'conversation.item.input_audio_transcription.completed':
          if (event.transcript !== undefined) {
            dispatchEvent(new Event(CONTAINER_EVENT.STOP_ANSWER));
            const _audio = new Audio(
              'https://mindverseglobal.mindverse.com/front-img/crm/1750413588327/pop-on-269286.mp3'
            );

            _audio.play().catch((err) => console.error('Error:', err));
            handleSendMsg(event.transcript, { voiceChannel: true });
            setEnabled(false);
            setStateText('');
            recordingEnableToggle(false);
          }

          break;
      }
    },
    [handleSendMsg]
  );

  const toggleMicrophone = () => {
    setEnabled((prev) => {
      const target = !prev;

      recordingEnableToggle(target);
      setStateText(target ? 'You can speak now' : '');

      return target;
    });
  };

  const handleConnectionStateChange = (state: RTCPeerConnectionState) => {
    console.log('state change', state);

    if (state === 'connected') {
      const audio = new Audio(
        'https://mindverseglobal.mindverse.com/front-img/crm/1743042814414/level-up-3-%E6%8E%A5%E9%80%9A3.mp3'
      );

      audio.play().catch((error) => console.error('Error:', error));
      setEnabled(true);
    } else if (state === 'disconnected' || state === 'failed') {
      //
    }
  };

  const handleError = (err: Error) => {
    console.log(err);
    message.error(err.message);
  };

  // 使用新版本的自定义Hook
  const { connect, disconnect, isConnected, isConnecting, recordingEnableToggle } =
    useOpenAIRealtimeTranscription({
      onEvent: handleEvent,
      onConnectionStateChange: handleConnectionStateChange,
      onError: handleError
    });

  useEffect(() => {
    const handleAnswered = () => {
      setStateText(`You can speak now`);
      setEnabled(true);
      recordingEnableToggle(true);
    };

    addEventListener(CONTAINER_EVENT.ANSWERED, handleAnswered);

    return () => {
      removeEventListener(CONTAINER_EVENT.ANSWERED, handleAnswered);
    };
  }, []);

  if (inputShowLogin) {
    return (
      <div
        className={classNames(
          styles.chatContainer,
          'w-full max-w-[756px] mt-[24px] !h-[44px] flex items-center p-1 gap-[4px] bg-[rgba(0,0,0,0.5)] rounded-[46px] backdrop-blur-[80px]  border-[1px] border-solid border-[rgba(254,253,255,0.2)] cursor-pointer'
        )}
        onClick={() => {
          dispatchEvent(
            new CustomEvent(EVENT.HOMEPAGE_SHOW_LOGIN_MODAL, {
              detail: { open: true }
            })
          );
        }}
      >
        <Icon className="text-[24px] p-[6px] text-[rgba(254,253,255,0.5)]" component={ChatSvg} />
        <div className={classNames('text-base leading-7 text-[rgba(254,253,255,0.5)]')}>
          {t('login.noLoginTips')}
        </div>
        <Icon
          className="text-[24px] ml-auto p-[6px] text-[rgba(254,253,255,0.5)]"
          component={RightSvg}
        />
      </div>
    );
  }

  if (type === 'keyboard') {
    return (
      <div className={classNames(styles.userInput, { [styles.inMobile]: inMobile })}>
        <div className={classNames(styles.inputKeyboard, 'relative z-10')}>
          {showQuickNote && (
            <QuickNote onHandleClick={handleShortCutClick} visible={shortcutShow} />
          )}

          {chatType === 'homepage' &&
          homepageData &&
          userId &&
          !homepageData.isBlocked &&
          !homepageData.blockedByMe &&
          !['official_product_assistant'].includes(homepageData.route || '') ? (
            <IceBreak className="mb-4 mx-auto" />
          ) : null}

          {/* {showHomepageFriendBtns && bubbleData && !bubbleLoading ? (
            <HomepageBubble
              className="mb-[10px]"
              isBubbleClosed={isBubbleClosed}
              onClose={() =>
                setBubbleData((prev) => {
                  if (prev) {
                    return { ...prev, isUsed: true };
                  }

                  return null;
                })
              }
              origin="homepage"
            />
          ) : null} */}

          {circleDetail?.route || homepageData?.userId ? (
            <div className="flex justify-start mb-[8px] gap-[8px]">
              {homepageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.FRIEND && <AutoReply />}
              <div className={styles.miniAvatars}>
                {historyValues.length <= 1 ? null : (
                  <Icon
                    className={classNames(styles.returnIcon, {
                      [styles.returnIconDisabled]: tempValueIndex <= 0,
                      [styles.returnHidden]: takeoverLoading
                    })}
                    component={ReturnSVG}
                    onClick={handlePrevVersion}
                  />
                )}
                {historyValues.length <= 1 || tempValueIndex >= historyValues.length - 1 ? null : (
                  <Icon
                    className={classNames(styles.returnIcon, styles.returnIconReverse, {
                      [styles.returnHidden]: takeoverLoading
                    })}
                    component={ReturnSVG}
                    onClick={handleNextVersion}
                  />
                )}
              </div>
              {showSummaryButton && (
                <div className={styles.summaryWrap}>
                  <button
                    className={styles.summary}
                    onClick={() => {
                      isSummaryRef.current = true;
                      handleTakeoverStream();
                    }}
                    type="button"
                  >
                    <span className={styles.summaryIconWrap}>
                      <span className={styles.summaryIcon}>💡</span>
                    </span>
                    <span className={styles.summaryText}>{t('circle.summarizeMessage')}</span>
                  </button>
                </div>
              )}

              <div
                // TODO 删掉右侧原本的玩法按钮后，我来 的位置也要调整
                className={classNames(
                  styles.takeoverContainer,
                  homepageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.FRIEND
                    ? showSummaryButton
                      ? 'mr-[30px]'
                      : 'mr-[70px]'
                    : 'mr-[20px]',
                  typingLabel && tempValueIndex < historyValues.length - 1
                    ? homepageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.FRIEND
                      ? 'mr-[24px]'
                      : 'mr-[-16px]'
                    : undefined
                )}
              >
                <button
                  className={classNames(styles.takeoverTrigger, {
                    [styles.takeoverActive]: takeoverLoading
                  })}
                  onClick={handleTakeoverStream}
                  type="button"
                >
                  {typingLabel.length > 0 ? (
                    <span ref={takeoverBubbleRef} className={styles.takeoverBubble}>
                      {typingLabel}
                    </span>
                  ) : null}
                  <div className={styles.takeoverAvatar}>
                    <RiveComponent />
                  </div>
                </button>
              </div>
            </div>
          ) : null}

          {quote && (
            <QuoteContainer
              className="mb-2"
              clickCloseQuote={() => {
                clearQuote();
              }}
              quote={quote}
            />
          )}

          <div
            className={classNames(
              'flex items-center transition-all align-stretch duration-300 ease-in-out'
            )}
          >
            <div
              className={classNames(styles.inputContainer, {
                [styles.mebot]: !homepageData && !circleDetail,
                [styles.containerLoading]: takeoverLoading
              })}
              id={EChatAppIds.INPUT_CONTAINER}
            >
              {takeoverLoading ? (
                <LoadingOutlined className={styles.messageIcon} />
              ) : showAddIcon ? (
                <AddPopover
                  disabled={!userId}
                  onUploadChange={handleUploadChange}
                  onUploadClick={handleUploadClick}
                  showPlayOptions={!!showHomepageFriendBtns}
                >
                  {(isOpen) => (
                    <div
                      className={classNames(styles.uploadTrigger, {
                        [styles.uploadTriggerActive]: isOpen
                      })}
                    >
                      <Icon
                        className="!text-[24px] color-[rgba(254, 253, 255, 0.50)] "
                        component={AddSvg}
                      />
                    </div>
                  )}
                </AddPopover>
              ) : !userId ? (
                <Icon
                  className="!text-[24px] self-center text-[rgba(254,253,255)]"
                  component={ChatSvg}
                />
              ) : null}

              <div className={styles.inputMain}>
                <MultimodalInput
                  ref={textareaRef}
                  disabled={sendMsgLoading || takeoverLoading}
                  inMobile={inMobile}
                  inputValue={inputValue}
                  isPA={isPA}
                  onBlur={handleOnBlur}
                  onDelFile={handleDelFile}
                  onFocus={handleOnFocus}
                  onInputChange={handleInputChange}
                  onMentionSelect={onMentionSelect}
                  onPaste={handleOnPaste}
                  onPressEnter={handlePressEnterWithSummary}
                  onUploadFiles={handleUploadFiles}
                  quote={quote}
                  takeoverLoading={takeoverLoading}
                  upList={upList}
                />
              </div>

              {takeoverLoading ? (
                <button className={styles.stopButton} onClick={handleStop} type="button">
                  <div className={styles.stopIcon} />
                </button>
              ) : sendMsgLoading || anonymousInitLoading ? (
                <SmallDotsLoading className={styles.sendLoading} />
              ) : (
                <>
                  {/* Send Button */}
                  <div
                    className={classNames(styles.sendMsg, { [styles.disabled]: hideSend })}
                    onClick={
                      hideSend
                        ? undefined
                        : () => {
                            setHistoryValues(['']);
                            setTempValueIndex(0);
                            handleIconSendWithSummary();
                          }
                    }
                  >
                    <Icon className={styles.sendMsgIcon} component={SendSvg} />
                  </div>
                </>
              )}
            </div>

            {/* {showHomepageFriendBtns ? (
              <div
                className={classNames(
                  'flex items-center justify-center ml-2 cursor-pointer transition-all duration-300 backdrop-blur-[40px]',
                  'bg-[rgba(32,32,32,0.2)] border-[0.5px] border-[rgba(254,253,255,0.2)] rounded-[40px]',
                  'w-[44px] h-[44px]'
                )}
                onClick={() => setDrawerOpen((prev) => !prev)}
              >
                <Icon
                  className="text-white text-[20px]"
                  component={drawerOpen ? CloseSvg : StarSvg}
                />
              </div>
            ) : null} */}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.userInputRecording}>
      <div
        className={styles.recordingContainer}
        onClick={() => {
          if (isConnecting) {
            message.info('Please wait until Connected');

            return;
          }

          dispatchEvent(new Event(EVENT.AGENT_SHARE_COLLAPSE));

          if (!isConnected) {
            connect();

            return;
          }

          toggleMicrophone();
        }}
      >
        <div
          className={classNames(styles.recordingBtn, {
            [styles.recordingActive]: isConnected && enabled
          })}
        >
          <Icon className={styles.recordingBtnIcon} component={RecordingSvg} />
        </div>
        <div className={styles.tips}>
          {enabled && isConnected ? stateText : isConnecting ? 'Connecting...' : ''}
        </div>
      </div>
      <div
        className={styles.keyboardBtn}
        onClick={() => {
          disconnect();
          setStateText('');
          setType('keyboard');
        }}
      >
        <Icon className={styles.keyboardBtnIcon} component={KeyboardSvg} />
      </div>
    </div>
  );
}

export default memo(UserInput);
